import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css, ThemeProps } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    customCSS = "customCSS",
    selectColumn = "selectColumn",
    selectGap = "selectGap",
    topHeader = "topHeader",
}
interface CustomLinkModel {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
        img: string;
    };
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: CustomLinkModel[];
        [fields.customCSS]: string;
        [fields.selectColumn]: number;
        [fields.selectGap]: number;
        [fields.topHeader]: string;
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    img?: InjectableCss;
    innerimg?: InjectableCss;
    topHeader?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    topHeader: {
        css: css`
            display: flex;
            justify-content: center;
            font-family: "Playfair Display";
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: 36px;
            line-height: 40px;
            text-align: center;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            color: #2c2420;
        `,
    },
    linkListWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 10px 0;
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    title: {
        css: css`
            margin-top: 16px;
            color: black !important;
        `,
    },
    linkWrapper: {
        css: css`
            padding: 1px 18px 0 0;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: unset !important;
                        `,
                        css`
                            width: unset !important;
                        `,
                        css`
                            width: unset !important;
                        `,
                        css`
                            width: unset !important;
                        `,
                    ],
                    "minMax",
                )};

            span {
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.small};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
                text-align: center;
                text-transform: uppercase;
                color: ${getColor("text.main")} !important;
                &:hover {
                }
            }
        `,
    },
    img: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [css``, css``, css``, css``], "minMax")};
        `,
    },
    innerimg: {
        css: css`
            object-fit: cover;
            aspect-ratio: 4.1/4.1 px;

            display: flex;
        `,
    },
};

const defaultCustomCss = `.list-wrapper {
}

.title-wrapper {
}

.list-title {
}

.links-wrapper {
}

.list-link {
}
`;

export const CategorySlides: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination).links;
    const titleLink = useGetLink(fields.titleLink);
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);

    const alignmentStyles = {
        css: css`
            display: flex;
            margin: auto;
            justify-content: flex-start;
            width: 100%;
            margin-top: 32px;
            margin-bottom: 32px;
            flex-wrap: wrap;
            gap: ${fields.selectGap}px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            gap: 25px;
                            display: flex;
                            flex-wrap: wrap;
                            justify-content: center;
                            margin-top: 20px;
                            margin-bottom: 20px;
                            margin-left: 0;
                            margin-right: 0;
                        `,
                        css`
                            margin-top: 20px;
                            gap: 25px;
                            display: flex;
                            flex-wrap: wrap;
                            justify-content: center;
                        `,
                        css`
                            margin-top: 20px;
                            gap: 25px;
                            display: flex;
                            flex-wrap: wrap;
                            justify-content: center;
                        `,
                        css`
                            justify-content: space-evenly;
                        `,
                        null,
                    ],
                    "minMax",
                )}
        `,
    };

    const customCssWrapper = {
        css: css`
            display: flex;
            justify-content: space-between;
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.topHeader}>Featured</StyledWrapper>
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper className="list-wrapper" {...alignmentStyles}>
                    {links.map((link, index) => (
                        <StyledWrapper
                            className="links-wrapper"
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            {...styles.wrapper}
                            style={{ width: `calc(100% / ${fields.selectColumn} - ${fields.selectGap}px)` }}
                        >
                            <StyledWrapper {...styles.img}>
                                <Clickable
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                >
                                    <LazyImage {...styles.innerimg} src={fields.links[index].fields.img}></LazyImage>
                                </Clickable>
                            </StyledWrapper>
                            {link.url && (
                                <Link
                                    className="list-link"
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                    {...styles.title}
                                >
                                    {fields.links[index].fields.overriddenTitle || link.title}
                                </Link>
                            )}
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: CategorySlides,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.selectColumn,
                displayName: "Select Column Width",
                editorTemplate: "DropDownField",
                defaultValue: 2,
                options: [1, 2, 3, 4, 5, 6, 7].map(value => ({ value })),
                fieldType: "General",
            },
            {
                name: fields.selectGap,
                displayName: "Select Column Gap",
                editorTemplate: "DropDownField",
                defaultValue: 2,
                options: [10, 20, 30, 40, 50, 60, 70].map(value => ({ value })),
                fieldType: "General",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "img",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        displayName: "Background Image",
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
